import { useEffect, useRef, useState, useCallback } from "react";
import type { Segment } from "@/lib/studio/segments";
import { hitTest, rasterizeSegment, fillSegment, strokeSegment } from "@/lib/studio/segments";
import { applyColorToBuffer, applyLighting, type PaintFinish, type LightingMode } from "@/lib/studio/preview-engine";

export type CanvasAssignments = Record<string, string>; // segmentId -> hex
export type CanvasFinishes = Record<string, PaintFinish>; // segmentId -> finish
export type SelectMode = "replace" | "add" | "remove" | "connected";

const MAX_DIM = 1280;

export function PaintCanvas({
  sourceUrl,
  segments,
  assignments,
  finishes,
  lighting = "natural",
  selected,
  externalHoverId,
  onSelect,
  onClickEmpty,
}: {
  sourceUrl: string;
  segments: Segment[];
  assignments: CanvasAssignments;
  finishes?: CanvasFinishes;
  lighting?: LightingMode;
  selected: string[];
  /** Segment id being hovered from outside the canvas (e.g. the surfaces list). */
  externalHoverId?: string | null;
  onSelect: (id: string, mode: SelectMode) => void;
  onClickEmpty?: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const srcRef = useRef<Uint8ClampedArray | null>(null);
  const dimsRef = useRef<{ w: number; h: number }>({ w: 0, h: 0 });
  const masksRef = useRef<Record<string, Uint8ClampedArray>>({});
  const rafRef = useRef<number | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  const activeHover = externalHoverId ?? hovered;
  const [ready, setReady] = useState(false);

  // Load image
  useEffect(() => {
    let cancelled = false;
    setReady(false);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (cancelled) return;
      const k = Math.min(1, MAX_DIM / Math.max(img.naturalWidth, img.naturalHeight));
      const w = Math.max(1, Math.round(img.naturalWidth * k));
      const h = Math.max(1, Math.round(img.naturalHeight * k));
      dimsRef.current = { w, h };
      const c = canvasRef.current;
      if (!c) return;
      c.width = w; c.height = h;
      const tmp = document.createElement("canvas");
      tmp.width = w; tmp.height = h;
      const tctx = tmp.getContext("2d")!;
      tctx.drawImage(img, 0, 0, w, h);
      srcRef.current = new Uint8ClampedArray(tctx.getImageData(0, 0, w, h).data);
      masksRef.current = {};
      setReady(true);
    };
    img.onerror = () => { /* leave un-ready */ };
    img.src = sourceUrl;
    return () => { cancelled = true; };
  }, [sourceUrl]);

  // Rasterise masks when segments change (after image is ready)
  useEffect(() => {
    if (!ready) return;
    const { w, h } = dimsRef.current;
    const next: Record<string, Uint8ClampedArray> = {};
    for (const s of segments) next[s.id] = rasterizeSegment(s, w, h);
    masksRef.current = next;
    schedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segments, ready]);

  const doRender = useCallback(() => {
    const c = canvasRef.current;
    const src = srcRef.current;
    const { w, h } = dimsRef.current;
    if (!c || !src || !w) return;
    const ctx = c.getContext("2d");
    if (!ctx) return;

    const out = new Uint8ClampedArray(src);
    for (const seg of segments) {
      const hex = assignments[seg.id];
      if (!hex) continue;
      const mask = masksRef.current[seg.id];
      if (!mask) continue;
      applyColorToBuffer(out, src, mask, hex, finishes?.[seg.id] ?? "matte");
    }
    if (lighting !== "natural") applyLighting(out, lighting);
    ctx.putImageData(new ImageData(out, w, h), 0, 0);

    // Clear visual affordance for what will be painted:
    //  - Hovered (unselected) segment: soft amber fill + dashed outline.
    //  - Selected segments: stronger amber fill + solid bold outline so
    //    the user can see the exact region the click captured, even when
    //    the geometric mask doesn't perfectly match the real-world wall.
    for (const seg of segments) {
      const isSelected = selected.includes(seg.id);
      const isHover = activeHover === seg.id && !isSelected;
      if (!isSelected && !isHover) continue;
      ctx.save();
      if (isSelected) {
        ctx.fillStyle = "rgba(252, 211, 77, 0.22)";
        fillSegment(ctx, seg, w, h);
        // Dark halo underneath for contrast on light surfaces.
        ctx.lineWidth = Math.max(4, Math.round(w / 260));
        ctx.strokeStyle = "rgba(0, 0, 0, 0.55)";
        strokeSegment(ctx, seg, w, h);
        ctx.lineWidth = Math.max(2, Math.round(w / 400));
        ctx.strokeStyle = "rgba(252, 211, 77, 1)";
        strokeSegment(ctx, seg, w, h);
      } else {
        ctx.fillStyle = "rgba(252, 211, 77, 0.16)";
        fillSegment(ctx, seg, w, h);
        ctx.setLineDash([8, 6]);
        ctx.lineWidth = Math.max(2, Math.round(w / 500));
        ctx.strokeStyle = "rgba(252, 211, 77, 0.9)";
        strokeSegment(ctx, seg, w, h);
      }
      ctx.restore();
    }
  }, [assignments, finishes, lighting, activeHover, segments, selected]);

  const schedule = useCallback(() => {
    if (rafRef.current != null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      doRender();
    });
  }, [doRender]);

  useEffect(() => { schedule(); }, [schedule]);

  function getXY(e: React.PointerEvent | React.MouseEvent): { x: number; y: number } | null {
    const c = canvasRef.current;
    if (!c) return null;
    const rect = c.getBoundingClientRect();
    const nx = (e.clientX - rect.left) / rect.width;
    const ny = (e.clientY - rect.top) / rect.height;
    if (nx < 0 || nx > 1 || ny < 0 || ny > 1) return null;
    return { x: nx, y: ny };
  }

  return (
    <div className="relative w-full">
      <canvas
        ref={canvasRef}
        className="block w-full h-auto rounded-2xl bg-zinc-900 select-none cursor-crosshair touch-manipulation"
        style={{ touchAction: "manipulation" }}
        onPointerMove={(e) => {
          const p = getXY(e);
          if (!p) { setHovered(null); return; }
          const hit = hitTest(segments, p.x, p.y);
          setHovered((cur) => (cur === (hit?.id ?? null) ? cur : hit?.id ?? null));
        }}
        onPointerLeave={() => setHovered(null)}
        onClick={(e) => {
          const p = getXY(e); if (!p) return;
          const hit = hitTest(segments, p.x, p.y);
          if (!hit) { onClickEmpty?.(); return; }
          const mode: SelectMode = e.shiftKey ? "add" : (e.ctrlKey || e.metaKey) ? "remove" : "replace";
          onSelect(hit.id, mode);
        }}
        onDoubleClick={(e) => {
          const p = getXY(e); if (!p) return;
          const hit = hitTest(segments, p.x, p.y);
          if (hit) onSelect(hit.id, "connected");
        }}
      />
      {!ready && (
        <div className="absolute inset-0 grid place-items-center text-zinc-500 text-sm pointer-events-none">
          Loading photo…
        </div>
      )}
    </div>
  );
}